import { useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import {
  useClientPortalNotifications,
  useDismissPortalNotification,
  useMarkAllPortalNotificationsRead,
  useMarkPortalNotificationRead,
} from '@/hooks/useClientPortalNotifications'
import { cn } from '@/lib/utils'
import { BellIcon, Check } from '@phosphor-icons/react'
import { useNavigate } from 'react-router-dom'

import { ClientPortalNotificationItem } from './ClientPortalNotificationItem'

interface ClientPortalNotificationCenterProps {
  entityId?: string | null
  className?: string
}

export function ClientPortalNotificationCenter({
  entityId,
  className,
}: ClientPortalNotificationCenterProps) {
  const [isOpen, setIsOpen] = useState(false)
  const navigate = useNavigate()
  const notificationsQuery = useClientPortalNotifications(entityId)
  const markRead = useMarkPortalNotificationRead(entityId)
  const markAllRead = useMarkAllPortalNotificationsRead(entityId)
  const dismiss = useDismissPortalNotification(entityId)

  if (!entityId) return null

  const items = notificationsQuery.data?.items ?? []
  const unreadCount = notificationsQuery.data?.unreadCount ?? 0
  const badgeLabel = unreadCount > 99 ? '99+' : String(unreadCount)

  const openNotifications = () => {
    setIsOpen(false)
    navigate(`/client-portal/${entityId}/notifications`)
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={cn(
            'relative h-8 w-8 rounded-lg transition-colors hover:bg-muted/60',
            className,
          )}
          aria-label={
            unreadCount > 0
              ? `Notifications, ${unreadCount} unread`
              : 'Notifications'
          }
          title="Notifications"
        >
          <BellIcon
            className="h-5 w-5"
            weight={unreadCount > 0 ? 'fill' : 'regular'}
          />
          {unreadCount > 0 ? (
            <span className="absolute -right-0.5 -top-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full border-2 border-background bg-destructive px-1 text-[10px] font-bold leading-none text-destructive-foreground shadow-sm">
              {badgeLabel}
            </span>
          ) : null}
        </Button>
      </PopoverTrigger>

      <PopoverContent
        align="end"
        sideOffset={6}
        className="w-[min(390px,calc(100vw-24px))] overflow-hidden p-0"
      >
        <div className="flex items-center justify-between px-4 py-3.5">
          <div>
            <h2 className="text-sm font-semibold tracking-tight">
              Notifications
            </h2>
            <p className="text-xs text-muted-foreground">
              Updates from your shared workspace
            </p>
          </div>
          {unreadCount > 0 ? (
            <span className="rounded-full bg-primary/10 px-2 py-1 text-[11px] font-semibold text-primary">
              {unreadCount} unread
            </span>
          ) : null}
        </div>

        <Separator />

        <div className="max-h-[480px] overflow-y-auto overscroll-contain p-2">
          {notificationsQuery.unavailable ? (
            <div role="alert" className="space-y-2 p-4 text-sm">
              <p>Couldn&apos;t load notifications.</p>
              <Button
                variant="outline"
                size="sm"
                disabled={notificationsQuery.isFetching}
                onClick={() => {
                  void notificationsQuery.refetch().catch(() => {})
                }}
              >
                Refresh notifications
              </Button>
            </div>
          ) : notificationsQuery.isLoading ? (
            <div className="space-y-2 p-1">
              <Skeleton className="h-20 w-full rounded-lg" />
              <Skeleton className="h-20 w-full rounded-lg" />
              <Skeleton className="h-20 w-full rounded-lg" />
            </div>
          ) : items.length > 0 ? (
            <div className="space-y-1">
              {items.map(item => (
                <ClientPortalNotificationItem
                  key={item.id}
                  item={item}
                  onOpen={() => {
                    if (
                      !item.readAt &&
                      !notificationsQuery.readOnly &&
                      !notificationsQuery.unavailable
                    )
                      markRead.mutate(item.id)
                    setIsOpen(false)
                    navigate(item.href)
                  }}
                  onDismiss={() => dismiss.mutate(item.id)}
                  dismissDisabled={
                    notificationsQuery.readOnly ||
                    notificationsQuery.unavailable ||
                    dismiss.isPending
                  }
                />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center px-6 py-10 text-center">
              <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-full border border-border/60 bg-muted/40 text-muted-foreground">
                <BellIcon className="h-5 w-5" />
              </div>
              <p className="text-sm font-medium">You&apos;re all caught up</p>
              <p className="mt-1 max-w-[250px] text-xs leading-relaxed text-muted-foreground">
                Updates you can access in this portal appear here.
              </p>
            </div>
          )}
        </div>

        <Separator />

        <div className="flex items-center justify-between p-2">
          {unreadCount > 0 ? (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 gap-1.5 text-xs text-muted-foreground"
              onClick={() => markAllRead.mutate()}
              disabled={
                markAllRead.isPending ||
                notificationsQuery.unavailable ||
                notificationsQuery.readOnly
              }
            >
              <Check className="h-3.5 w-3.5" />
              Mark all read
            </Button>
          ) : (
            <span />
          )}
          <Button
            variant="ghost"
            size="sm"
            className="h-8 text-xs text-primary hover:text-primary"
            onClick={openNotifications}
          >
            View all notifications
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
